/* =========================================================================
   useTelemetryTable.ts
   Custom hook encapsulating container measurement, 2D virtualization,
   infinite historical scroll pagination, and column/timestamp metadata.
   ========================================================================= */

import { useState, useEffect, useRef, useMemo, useCallback, useContext } from 'react';
import {
  resolveTableColumns,
  tableColLabel,
  getTableTimestamps,
  fmtClock,
  colKey,
} from '../utils/telemetry-helpers.utils';
import { filterTableColumnsByQuery, filterTableTimestampsByQuery } from '../utils/telemetry-search.utils';
import {
  DEFAULT_ROW_HEIGHT,
  DEFAULT_COL_WIDTH,
  DEFAULT_TIME_COL_WIDTH,
  OVERSCAN_ROWS,
  OVERSCAN_COLS,
  detectTableScrollBoundary,
} from '../utils/telemetry-table.utils';
import { TelemetryLayoutContext } from '../components/TelemetryLayout';

export interface UseTelemetryTableOptions {
  panel: any;
  mode: 'ctable' | 'table';
  units: any;
  precision: number;
  timeframe?: number;
  viewport?: { spanMs: number; offsetMs: number };
  rowHeight?: number;
  colWidth?: number;
  timeColWidth?: number;
}

export interface UseTelemetryTableReturn {
  containerRef: React.RefObject<HTMLDivElement>;
  handleScroll: (e: React.UIEvent<HTMLDivElement>) => void;
  visibleCols: Array<{ index: number; label: string }>;
  visibleRowIndices: number[];
  visibleColumns: any[];
  timestamps: number[];
  isStream: boolean;
  headerLeft: string;
  firstColWidth: number;
  dataColWidth: number;
  leftPadCols: number;
  rightPadCols: number;
  topPadRows: number;
  bottomPadRows: number;
  rowHeight: number;
}

export function useTelemetryTable(options: UseTelemetryTableOptions): UseTelemetryTableReturn {
  const {
    panel,
    timeframe,
    viewport,
    rowHeight = DEFAULT_ROW_HEIGHT,
    colWidth = DEFAULT_COL_WIDTH,
    timeColWidth = DEFAULT_TIME_COL_WIDTH,
  } = options;

  const layoutCtx = useContext(TelemetryLayoutContext);
  const activeViewport = viewport ?? layoutCtx?.viewport ?? { spanMs: 300000, offsetMs: 0 };
  const onViewportChange = layoutCtx?.onViewportChange;
  const isLoadingHistory = layoutCtx?.isLoadingHistory ?? false;
  const isAtStartBoundary = layoutCtx?.isAtStartBoundary ?? false;
  const activeTimeframe = timeframe ?? layoutCtx?.timeframe ?? 5;

  const containerRef = useRef<HTMLDivElement>(null);
  const [containerSize, setContainerSize] = useState({ width: 600, height: 400 });
  const [scrollPos, setScrollPos] = useState({ scrollTop: 0, scrollLeft: 0 });
  const prevScrollPosRef = useRef({ st: 0, sl: 0 });
  const lastFetchTriggerTimeRef = useRef<number>(0);
  const animFrameRef = useRef<number | null>(null);

  // 1. Measure container dimensions via ResizeObserver
  useEffect(() => {
    const node = containerRef.current;
    if (!node) return;

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        if (entry.contentRect) {
          setContainerSize({
            width: entry.contentRect.width || node.clientWidth || 600,
            height: entry.contentRect.height || node.clientHeight || 400,
          });
        }
      }
    });

    observer.observe(node);
    setContainerSize({
      width: node.clientWidth || 600,
      height: node.clientHeight || 400,
    });

    return () => observer.disconnect();
  }, []);

  // 2. Throttled scroll handler with historical pagination trigger
  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const target = e.currentTarget;
    const st = target.scrollTop;
    const sl = target.scrollLeft;

    if (animFrameRef.current !== null) {
      cancelAnimationFrame(animFrameRef.current);
    }

    animFrameRef.current = requestAnimationFrame(() => {
      setScrollPos({ scrollTop: st, scrollLeft: sl });
      animFrameRef.current = null;
    });

    const isStream = panel?.orient === 'rows';
    const isAsc = panel?.order === 'asc';
    const { hasReachedEnd, hasReachedStart } = detectTableScrollBoundary(
      target,
      prevScrollPosRef.current.st,
      prevScrollPosRef.current.sl,
      isStream,
      isAsc
    );

    prevScrollPosRef.current = { st, sl };

    const now = Date.now();
    const isLockedMode =
      (activeViewport as any)?.customStartMs != null ||
      (layoutCtx?.customStartTimeSec != null && layoutCtx.customStartTimeSec > 0);

    if (!isLockedMode) {
      if (
        hasReachedEnd &&
        !isLoadingHistory &&
        !isAtStartBoundary &&
        onViewportChange &&
        now - lastFetchTriggerTimeRef.current > 1200
      ) {
        lastFetchTriggerTimeRef.current = now;
        const currentOffset = activeViewport.offsetMs || 0;
        const spanMs = activeViewport.spanMs || (activeTimeframe * 60000);
        onViewportChange({ offsetMs: currentOffset + spanMs });
      }

      if (
        hasReachedStart &&
        (activeViewport.offsetMs || 0) > 0 &&
        onViewportChange &&
        now - lastFetchTriggerTimeRef.current > 300
      ) {
        lastFetchTriggerTimeRef.current = now;
        onViewportChange({ offsetMs: 0 });
      }
    }
  }, [panel?.orient, panel?.order, isLoadingHistory, isAtStartBoundary, onViewportChange, activeViewport, activeTimeframe, layoutCtx?.customStartTimeSec]);

  useEffect(() => {
    return () => {
      if (animFrameRef.current !== null) {
        cancelAnimationFrame(animFrameRef.current);
      }
    };
  }, []);

  // 3. Scroll to top when jumpToLive resets offsetMs to 0 or orientation/order changes
  const prevOffsetRef = useRef(activeViewport.offsetMs);
  useEffect(() => {
    if (prevOffsetRef.current > 0 && activeViewport.offsetMs === 0 && containerRef.current) {
      containerRef.current.scrollTop = 0;
      containerRef.current.scrollLeft = 0;
    }
    prevOffsetRef.current = activeViewport.offsetMs;
  }, [activeViewport.offsetMs]);

  const prevOrientRef = useRef(panel?.orient);
  const prevOrderRef = useRef(panel?.order);
  useEffect(() => {
    if (
      (prevOrientRef.current !== undefined && prevOrientRef.current !== panel?.orient) ||
      (prevOrderRef.current !== undefined && prevOrderRef.current !== panel?.order)
    ) {
      if (containerRef.current) {
        containerRef.current.scrollTop = 0;
        containerRef.current.scrollLeft = 0;
      }
      setScrollPos({ scrollTop: 0, scrollLeft: 0 });
    }
    prevOrientRef.current = panel?.orient;
    prevOrderRef.current = panel?.order;
  }, [panel?.orient, panel?.order]);

  const telemetrySeries = layoutCtx?.telemetrySeries;
  const isTelemetryDataLoading = layoutCtx?.isTelemetryDataLoading;
  const [dataVersion, setDataVersion] = useState(0);

  useEffect(() => {
    setDataVersion((v) => v + 1);
  }, [telemetrySeries, isTelemetryDataLoading, isLoadingHistory]);

  // 4. Fast Metadata Preparation
  const visibleColumns = useMemo(() => {
    const allCols = resolveTableColumns(panel);
    const unhidden = allCols.filter((c) => !(panel?.hiddenCols || []).includes(colKey(c)));
    return filterTableColumnsByQuery(unhidden, panel?.searchQuery);
  }, [panel?.parameters, panel?.searchQuery, panel?.hiddenCols]);

  const timestamps = useMemo(() => {
    const rawTimes = getTableTimestamps(panel, activeTimeframe, activeViewport);
    const sortedTimes = panel?.order === 'asc'
      ? [...rawTimes].sort((a, b) => a - b)
      : [...rawTimes].sort((a, b) => b - a);
    return filterTableTimestampsByQuery(sortedTimes, panel?.searchQuery, fmtClock);
  }, [
    panel?.parameters,
    panel?.hiddenCols,
    panel?.order,
    activeTimeframe,
    activeViewport?.offsetMs,
    activeViewport?.spanMs,
    panel?.searchQuery,
    isTelemetryDataLoading,
    isLoadingHistory,
    telemetrySeries,
    dataVersion,
  ]);

  const isStream = panel?.orient === 'rows';
  const totalRows = isStream ? timestamps.length : visibleColumns.length;
  const totalCols = isStream ? visibleColumns.length : timestamps.length;
  const headerLeft = isStream ? 'TIME (UTC)' : 'PARAMETER';

  const firstColWidth = isStream ? timeColWidth : colWidth;
  const dataColWidth = isStream ? colWidth : timeColWidth;

  // 5. 2D Virtualization Calculations
  const startRow = Math.max(0, Math.floor(scrollPos.scrollTop / rowHeight) - OVERSCAN_ROWS);
  const endRow = Math.min(totalRows, Math.ceil((scrollPos.scrollTop + containerSize.height) / rowHeight) + OVERSCAN_ROWS);

  const startCol = Math.max(0, Math.floor((scrollPos.scrollLeft - firstColWidth) / dataColWidth) - OVERSCAN_COLS);
  const endCol = Math.min(totalCols, Math.ceil((scrollPos.scrollLeft + containerSize.width - firstColWidth) / dataColWidth) + OVERSCAN_COLS);

  const visibleCols = useMemo(() => {
    const list: Array<{ index: number; label: string }> = [];
    for (let c = startCol; c < endCol; c++) {
      if (c >= 0 && c < totalCols) {
        const label = isStream
          ? tableColLabel(visibleColumns[c]).replace(/_/g, ' ')
          : fmtClock(timestamps[c]);
        list.push({ index: c, label });
      }
    }
    return list;
  }, [startCol, endCol, totalCols, isStream, visibleColumns, timestamps]);

  const visibleRowIndices = useMemo(() => {
    const list: number[] = [];
    for (let r = startRow; r < endRow; r++) {
      if (r >= 0 && r < totalRows) {
        list.push(r);
      }
    }
    return list;
  }, [startRow, endRow, totalRows]);

  const leftPadCols = startCol;
  const rightPadCols = Math.max(0, totalCols - endCol);
  const topPadRows = startRow;
  const bottomPadRows = Math.max(0, totalRows - endRow);

  return {
    containerRef,
    handleScroll,
    visibleCols,
    visibleRowIndices,
    visibleColumns,
    timestamps,
    isStream,
    headerLeft,
    firstColWidth,
    dataColWidth,
    leftPadCols,
    rightPadCols,
    topPadRows,
    bottomPadRows,
    rowHeight,
  };
}
